<template>
  <div
    :id="thisComment.id"
    class="tw-grid tw-grid-cols-[2.25rem_1fr] tw-gap-x-2 tw-gap-y-4 tw-relative"
    :class="{
      'tw-border-t-[2rem] tw-border-transparent':
        comment.level === 1 && thisComment.level !== comment.level,
    }"
  >
    <base-button
      v-if="comment.level === 1 && thisComment.level !== comment.level"
      text-link-hover
      no-padding
      class="tw-absolute tw-left-0 -tw-top-10"
      @click="reset"
    >
      <font-awesome-icon icon="chevron-left" class="tw-mr-1" />
      {{ $t('back') }}
    </base-button>

    <div class="tw-relative">
      <base-link
        :link-primary="false"
        :to="{
          name: USER_GAME_MOD_BROWSE_ROUTE,
          params: isFrontend
            ? {
                user: thisComment.user.name_id.toLowerCase(),
                game: gameNameId,
              }
            : { user: thisComment.user.name_id.toLowerCase() },
        }"
      >
        <avatar-icon
          show-status
          :user="thisComment.user"
          size="md"
          class="tw-self-start"
        />
      </base-link>
      <div
        v-if="thisComment.replies?.length && (commentAboveThreshold || show)"
        class="tw-w-px tw-bg-[var(--tile-text)] tw-opacity-30 tw-absolute tw-left-[1.125rem] tw-top-9 tw-h-[calc(100%-0.5rem)]"
      />
    </div>
    <div :class="Classes.COMMENT">
      <div class="tw-flex tw-items-center tw-leading-none tw-h-8 tw-space-x-2">
        <div
          class="tw-flex tw-items-center tw-space-x-2"
          :class="{ 'tw-opacity-70': read && !showComment }"
        >
          <base-link
            hover-primary
            :link-primary="false"
            :to="{
              name: USER_GAME_MOD_BROWSE_ROUTE,
              params: isFrontend
                ? {
                    user: thisComment.user.name_id.toLowerCase(),
                    game: gameNameId,
                  }
                : { user: thisComment.user.name_id.toLowerCase() },
            }"
          >
            <span class="tw-font-medium">{{ thisComment.user.username }}</span>
          </base-link>
          <base-badge
            v-if="
              thisComment.resource_ownership >=
              COMMENT_OWNERSHIP.GAME_TEAM_MEMBER
            "
            border
            color="dark"
            rounded="small"
            class="tw-uppercase"
            :text="
              thisComment.resource_ownership >= COMMENT_OWNERSHIP.SITE_ADMIN
                ? $t('site_admin')
                : $t('game_admin')
            "
          />
          <author-icon
            v-else-if="showAuthorIcon"
            :type="type"
            :user-name-id="thisComment.user.name_id"
          />
          <partner-icon v-if="thisComment.partner" discussion />
          <span class="tw-opacity-70">•</span>
        </div>
        <component
          :is="isFrontend && !modal ? 'a' : 'base-link'"
          v-bind="linkProps"
          class="hover:tw-text-primary hover:tw-opacity-100"
          :class="replied ? 'tw-opacity-100' : 'tw-opacity-70'"
        >
          <span v-if="replied" class="tw-text-success">
            {{ $t('replied') }}
          </span>
          <base-time-tooltip v-else :timestamp="thisComment.date_added">
            {{ timeSince(thisComment.date_added) }} {{ $t('ago') }}
          </base-time-tooltip>
        </component>
        <tooltip-hover v-if="isPinned && !modal" :text="$t('pinned')">
          <font-awesome-icon icon="thumbtack" />
        </tooltip-hover>
        <tooltip-hover v-if="isLocked && !modal" :text="$t('locked')">
          <font-awesome-icon icon="lock" />
        </tooltip-hover>
        <base-button
          v-if="modal && read"
          class="tw-right-0 tw-absolute"
          text-link-hover
          small
          @click="toggleComment()"
        >
          {{ showComment ? $t('close') : $t('reopen') }}
        </base-button>
      </div>
      <base-link
        v-if="resourceName && modal"
        :to="resourceTo"
        hover-primary
        class="mod-text-p-xs tw-flex tw-mt-1 tw-w-fit"
      >
        {{ resourceName }}
      </base-link>
      <template
        v-if="(commentAboveThreshold || show) && !(modal && !showComment)"
      >
        <!-- danger -->
        <div
          v-if="editId !== thisComment.id"
          ref="contentRef"
          class="tw-break-words mod-content mod-view-text tw-whitespace-pre-line tw-opacity-80 tw-mb-2"
          :class="{ 'tw-mt-2': showAuthorIcon || modal }"
          v-html="processedContent"
        />

        <discussion-card-footer
          v-if="canComment && !unverifiedAge"
          :modal="modal"
          :type="type"
          :read="read"
          :game-name-id="gameNameId"
          :resource-name-id="resourceNameId"
          :comment-id="thisComment.id"
          :level="thisComment.level"
          :is-owner="isOwner"
          :karma="thisComment.karma"
          :options="thisComment.options"
          :has-replies="!!thisComment.replies?.length"
          :can-downvote="
            gameAllowDownvote &&
            thisComment.resource_ownership === COMMENT_OWNERSHIP.USER &&
            !showAuthorIcon
          "
          @read="$emit('read')"
        >
          <template #default="{ toggle }">
            <slot
              :reply-id="thisComment.id"
              :comment-text="thisComment.content"
              :toggle="toggle"
            />
          </template>
        </discussion-card-footer>
      </template>
      <base-button
        v-else-if="!commentAboveThreshold"
        class="tw-opacity-70 tw-italic"
        text-link-hover
        no-padding
        @click="open"
      >
        {{ $t('comment_below_threshold') }}
      </base-button>
    </div>

    <template
      v-if="thisComment.replies?.length && (commentAboveThreshold || show)"
    >
      <template v-for="(reply, index) in thisComment.replies" :key="reply.id">
        <div v-if="!collapsed || index === 0" class="tw-relative">
          <base-button
            v-if="index === 0"
            text-link-hover
            primary
            overflow
            no-padding
            class="tw-ml-2.5 tw-mt-[0.71875rem] tw-text-[var(--tile-text)] tw-self-start !tw-leading-none"
            style="height: fit-content; border: 0"
            :icon="collapsed ? 'plus-circle' : 'minus-circle'"
            @click="clickExpand"
          >
            <span v-if="collapsed" class="tw-whitespace-nowrap">
              {{ replyText }}
            </span>
          </base-button>

          <div
            v-if="index < thisComment.replies?.length - 1 && !collapsed"
            class="tw-w-px tw-bg-[var(--tile-text)] tw-opacity-30 tw-ml-4.5"
            :style="{
              height:
                index === 0 && index === thisComment.replies?.length - 2
                  ? 'calc(100% + 0.5625rem)'
                  : index === 0
                    ? 'calc(100% - 0.5625rem)'
                    : 'calc(100% + 2.1875rem)',
            }"
          />
          <div
            v-if="!collapsed"
            class="tw-h-px tw-bg-[var(--tile-text)] tw-opacity-30 tw-absolute tw-top-[1.125rem]"
            :class="
              index === 0
                ? 'tw-left-[1.625rem] tw-w-5'
                : 'tw-left-[1.1875rem] tw-w-8'
            "
          />
        </div>
        <discussion-card
          v-if="!collapsed"
          :type="type"
          :game-name-id="gameNameId"
          :resource-name-id="resourceNameId"
          :comment="reply"
        >
          <template #default="{ replyId, commentText, toggle }">
            <slot
              :reply-id="replyId"
              :comment-text="commentText"
              :toggle="toggle"
            />
          </template>
        </discussion-card>
      </template>
    </template>
  </div>
</template>

<script>
import DiscussionCardFooter from '@shared/components/Discussion/DiscussionCardFooter.vue'
import { recurseDiscussionCount } from '@shared/js/stores/discussionStore.js'
import formatContent, { attachLinkEvents } from '@helpers/formatContent.js'
import { isCommentPinned, isCommentLocked } from '@helpers/bitHelper.js'
import AuthorIcon from '@shared/components/Discussion/AuthorIcon.vue'
import { HIDE_COMMENT_BELOW_KARMA, isFrontend, isEMH } from '@config'
import PartnerIcon from '@shared/components/Users/PartnerIcon.vue'
import TooltipHover from '@components/Message/TooltipHover.vue'
import { useCommentAuthors, useToggle } from '@composables'
import AvatarIcon from '@components/Users/AvatarIcon.vue'
import Classes from '@shared/js/config/css.js'
import { Settings } from '@config/game.js'
import { authStore } from '@stores'
import { useI18n } from 'vue-i18n'
import {
  DISCUSSION_TYPE_COLLECTIONS,
  DISCUSSION_TYPE_GUIDES,
  DISCUSSION_TYPE_MODS,
  DISCUSSION_TYPES,
} from '@config/options.js'
import {
  USER_GAME_MOD_BROWSE_ROUTE,
  COLLECTION_VIEW_ROUTE,
  GUIDE_VIEW_ROUTE,
  MOD_VIEW_ROUTE,
} from '@config/routeNames.js'
import {
  watchEffect,
  computed,
  nextTick,
  provide,
  toRefs,
  inject,
  watch,
  ref,
} from 'vue'
import {
  scrollToElementOffset,
  humanDateFormat,
  timeSince,
} from '@helpers/utils.js'

const COMMENT_OWNERSHIP = {
  USER: 0,
  RESOURCE_TEAM_MEMBER: 1,
  GAME_TEAM_MEMBER: 2,
  SITE_ADMIN: 4,
}

export default {
  components: {
    DiscussionCardFooter,
    TooltipHover,
    PartnerIcon,
    AuthorIcon,
    AvatarIcon,
  },
  props: {
    type: {
      validator: (val) => DISCUSSION_TYPES.includes(val),
      required: true,
    },
    gameNameId: {
      type: String,
      required: true,
    },
    resourceNameId: {
      type: String,
      required: true,
    },
    resourceName: {
      type: String,
      default: '',
    },
    comment: {
      type: Object,
      default: null,
    },
    modal: {
      type: Boolean,
      default: false,
    },
    replied: {
      type: Boolean,
      default: false,
    },
    read: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['read'],
  setup(props) {
    const { comment, gameNameId, modal, resourceNameId, replied, read, type } =
      toRefs(props)

    const focussedComment = ref(comment.value)
    const contentRef = ref(null)

    const { authors } = useCommentAuthors()
    const { show, open } = useToggle()
    const { show: showComment, toggle: toggleComment } = useToggle(
      !replied.value && !read.value
    )
    const { t } = useI18n()

    const { user: myUser, unverifiedAge } = authStore()

    const resourceTo = computed(() => {
      const params = {}
      let name = ''

      switch (type.value) {
        case DISCUSSION_TYPE_MODS:
          params.mod = resourceNameId.value
          name = MOD_VIEW_ROUTE
          break
        case DISCUSSION_TYPE_COLLECTIONS:
          params.collection = resourceNameId.value
          name = COLLECTION_VIEW_ROUTE
          break
        case DISCUSSION_TYPE_GUIDES:
          params.guide = resourceNameId.value
          name = GUIDE_VIEW_ROUTE
          break
      }

      if (isFrontend) {
        params.game = gameNameId.value
      }

      return {
        params,
        name,
      }
    })

    const linkProps = computed(() => {
      if (isFrontend && !modal.value) {
        return {
          href: `#${comment.value.id}`,
          onClick: (e) => {
            e.preventDefault()
            onIdClick(thisComment.value.id)
          },
        }
      } else if (isFrontend && modal.value) {
        return {
          to: {
            ...resourceTo.value,
            hash: `#${comment.value.id}`,
          },
          linkPrimary: false,
          onClick: () => onIdClick(thisComment.value.id),
        }
      } else {
        return {
          to: {
            query: {
              tab: 'discussion',
              comment: comment.value.id,
            },
          },
          linkPrimary: false,
          onClick: () => onIdClick(thisComment.value.id),
        }
      }
    })

    const thisComment = computed(() =>
      comment.value.level === 1 ? focussedComment.value : comment.value
    )

    const editId = inject('editId')
    if (comment.value.level === 1) {
      provide('focussedComment', focussedComment)
      provide('focusComment', (c) => (focussedComment.value = c))
    }
    const injectedComment = inject('focussedComment', focussedComment)
    const focusComment = inject('focusComment', () => {})
    const gameAllowDownvote = inject('gameAllowDownvote', true)

    watch(comment, (_comment) => {
      // Watch for new replies and edits
      const _commentId = focussedComment.value.id
      focussedComment.value = _findCommentById(_comment, _commentId) || _comment
    })

    const thisLevel = computed(
      () =>
        comment.value.level -
        (injectedComment.value.level > 1 ? injectedComment.value.level - 1 : 0)
    )
    const collapsed = ref(
      comment.value.replies?.length && thisLevel.value === 3
    )

    const canComment = computed(() => Settings.value.allowCommenting)
    const isPinned = computed(() => isCommentPinned(comment.value.options))
    const isLocked = computed(() => isCommentLocked(comment.value.options))

    const showAuthorIcon = computed(() =>
      authors.value.includes(thisComment.value.user.name_id)
    )

    const processedContent = computed(() =>
      thisComment.value.content
        ? formatContent(thisComment.value.content, thisComment.value.karma)
        : ''
    )

    if (isEMH) {
      watchEffect(() => {
        if (processedContent.value && contentRef.value)
          nextTick(() => attachLinkEvents(contentRef.value))
      })
    }

    const replyText = computed(() => {
      const replyCount =
        thisComment.value.replies?.length +
        thisComment.value.replies?.reduce(
          (count, reply) => count + recurseDiscussionCount(reply),
          0
        )
      return `${replyCount} ${t('replies', replyCount)}`
    })

    const isOwner = computed(
      () =>
        (myUser.value?.info?.name_id || myUser.value?.name_id) ===
        thisComment.value.user.name_id
    )

    const commentAboveThreshold = computed(
      () => thisComment.value.karma > HIDE_COMMENT_BELOW_KARMA
    )

    function clickExpand() {
      if (thisLevel.value % 3 === 0 && thisLevel.value > 0) {
        focusComment(comment.value)
      } else {
        collapsed.value = !collapsed.value
      }
    }

    function onIdClick(id) {
      if (isFrontend) {
        history.replaceState(null, null, `#${id}`)
      }
      scrollToElementOffset(id.toString())
    }

    function reset() {
      focussedComment.value = comment.value
    }

    function _findCommentById(comment, id) {
      if (comment.id === id) return comment

      if (comment.replies?.length) {
        for (const reply of comment.replies) {
          const foundComment = _findCommentById(reply, id)
          if (foundComment) return foundComment
        }
      }

      return null
    }

    watch(read, toggleComment)

    return {
      USER_GAME_MOD_BROWSE_ROUTE,
      commentAboveThreshold,
      gameAllowDownvote,
      COMMENT_OWNERSHIP,
      processedContent,
      humanDateFormat,
      MOD_VIEW_ROUTE,
      showAuthorIcon,
      unverifiedAge,
      toggleComment,
      thisComment,
      showComment,
      clickExpand,
      resourceTo,
      isFrontend,
      canComment,
      contentRef,
      linkProps,
      onIdClick,
      collapsed,
      replyText,
      thisLevel,
      timeSince,
      isPinned,
      isLocked,
      isOwner,
      Classes,
      editId,
      reset,
      show,
      open,
    }
  },
}
</script>
